import { getCategoryDisplayName, CF_CATEGORIES } from './freee-mapping.js';

// Chart.js is loaded from CDN in index.html
// Use global Chart object

/**
 * UI Manager for Cashflow Dashboard
 */
export class CashFlowUI {
  constructor() {
    this.charts = {};
    this.currentFiscalYear = null;
    this.monthlySummaries = null;
  }

  /**
   * Initialize the dashboard UI
   * @param {string} containerId - Container element ID
   */
  init(containerId) {
    this.container = document.getElementById(containerId);
    if (!this.container) {
      console.error(`Container with ID ${containerId} not found`);
      return;
    }
    
    // Check if Chart.js is loaded
    if (typeof Chart === 'undefined') {
      console.error('Chart.js is not loaded!');
      this.showError('Chart.jsの読み込みに失敗しました。ページをリロードしてください。');
      return;
    }
    
    console.log('Chart.js version:', Chart.version);
    this.render();
  }

  /**
   * Render the main dashboard structure
   */
  render() {
    this.container.innerHTML = `
      <div class="cashflow-dashboard">
        <div class="dashboard-header">
          <h1>キャッシュフロー可視化</h1>
          <div class="controls">
            <select id="fiscalYearSelect" class="fiscal-year-select">
              <option value="">会計年度を選択...</option>
            </select>
            <button id="refreshBtn" class="btn-refresh">更新</button>
          </div>
        </div>
        
        <div class="summary-cards" id="summaryCards">
          <!-- Summary cards will be inserted here -->
        </div>
        
        <div class="charts-container">
          <div class="chart-wrapper">
            <h2>月次推移</h2>
            <canvas id="monthlyTrendChart"></canvas>
          </div>
          
          <div class="chart-wrapper">
            <h2>カテゴリ別内訳</h2>
            <canvas id="categoryBreakdownChart"></canvas>
          </div>
        </div>
        
        <div class="detailed-breakdown" id="detailedBreakdown">
          <!-- Detailed breakdown will be inserted here -->
        </div>
      </div>
    `;
    
    this.attachEventListeners();
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    const fiscalYearSelect = document.getElementById('fiscalYearSelect');
    const refreshBtn = document.getElementById('refreshBtn');
    
    if (fiscalYearSelect) {
      fiscalYearSelect.addEventListener('change', (e) => {
        this.onFiscalYearChange(parseInt(e.target.value));
      });
    }
    
    if (refreshBtn) {
      refreshBtn.addEventListener('click', () => {
        this.onRefresh();
      });
    }
  }

  /**
   * Update fiscal year options
   * @param {Array} fiscalYears - Available fiscal years
   * @param {number} currentYear - Current fiscal year
   */
  updateFiscalYearOptions(fiscalYears, currentYear) {
    const select = document.getElementById('fiscalYearSelect');
    if (!select) return;
    
    select.innerHTML = fiscalYears.map(year => 
      `<option value="${year}" ${year === currentYear ? 'selected' : ''}>
        ${year}年度 (${year}年5月 - ${year + 1}年4月)
      </option>`
    ).join('');
    
    this.currentFiscalYear = currentYear;
  }

  /**
   * Update summary cards
   * @param {Object} yearSummary - Year cashflow summary
   */
  updateSummaryCards(yearSummary) {
    const cardsContainer = document.getElementById('summaryCards');
    if (!cardsContainer) return;
    
    const categories = [
      { key: CF_CATEGORIES.OPERATING, color: '#4CAF50' },
      { key: CF_CATEGORIES.INVESTING, color: '#2196F3' },
      { key: CF_CATEGORIES.FINANCING, color: '#FF9800' }
    ];
    
    const cardsHTML = categories.map(({ key, color }) => {
      const data = yearSummary[key];
      const netClass = data.net >= 0 ? 'positive' : 'negative';
      
      return `
        <div class="summary-card" style="border-left: 4px solid ${color}">
          <h3>${getCategoryDisplayName(key)}</h3>
          <div class="amount-row">
            <span class="label">収入:</span>
            <span class="amount positive">¥${this.formatNumber(data.inflow)}</span>
          </div>
          <div class="amount-row">
            <span class="label">支出:</span>
            <span class="amount negative">¥${this.formatNumber(data.outflow)}</span>
          </div>
          <div class="amount-row total">
            <span class="label">純額:</span>
            <span class="amount ${netClass}">¥${this.formatNumber(data.net)}</span>
          </div>
        </div>
      `;
    }).join('');
    
    // Add total card
    const total = yearSummary.total;
    const totalNetClass = total.net >= 0 ? 'positive' : 'negative';
    
    const totalCardHTML = `
      <div class="summary-card total-card">
        <h3>合計キャッシュフロー</h3>
        <div class="amount-row">
          <span class="label">収入:</span>
          <span class="amount positive">¥${this.formatNumber(total.inflow)}</span>
        </div>
        <div class="amount-row">
          <span class="label">支出:</span>
          <span class="amount negative">¥${this.formatNumber(total.outflow)}</span>
        </div>
        <div class="amount-row total">
          <span class="label">純額:</span>
          <span class="amount ${totalNetClass}">¥${this.formatNumber(total.net)}</span>
        </div>
      </div>
    `;
    
    cardsContainer.innerHTML = cardsHTML + totalCardHTML;
  }

  /**
   * Update monthly trend chart
   * @param {Array} monthlySummaries - Monthly cashflow summaries
   */
  updateMonthlyTrendChart(monthlySummaries) {
    this.monthlySummaries = monthlySummaries;
    
    const ctx = document.getElementById('monthlyTrendChart');
    if (!ctx) return;
    
    // Destroy existing chart
    if (this.charts.monthlyTrend) {
      this.charts.monthlyTrend.destroy();
    }
    
    const labels = monthlySummaries.map(m => `${m.month}月`);
    
    this.charts.monthlyTrend = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: getCategoryDisplayName(CF_CATEGORIES.OPERATING),
            data: monthlySummaries.map(m => m[CF_CATEGORIES.OPERATING].net),
            borderColor: '#4CAF50',
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            tension: 0.4
          },
          {
            label: getCategoryDisplayName(CF_CATEGORIES.INVESTING),
            data: monthlySummaries.map(m => m[CF_CATEGORIES.INVESTING].net),
            borderColor: '#2196F3',
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
            tension: 0.4
          },
          {
            label: getCategoryDisplayName(CF_CATEGORIES.FINANCING),
            data: monthlySummaries.map(m => m[CF_CATEGORIES.FINANCING].net),
            borderColor: '#FF9800',
            backgroundColor: 'rgba(255, 152, 0, 0.1)',
            tension: 0.4
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                return `${context.dataset.label}: ¥${this.formatNumber(context.parsed.y)}`;
              }
            }
          }
        },
        scales: {
          y: {
            ticks: {
              callback: (value) => '¥' + this.formatNumber(value)
            }
          }
        }
      }
    });
  }

  /**
   * Update category breakdown chart
   * @param {Object} yearSummary - Year cashflow summary
   */
  updateCategoryBreakdownChart(yearSummary) {
    const ctx = document.getElementById('categoryBreakdownChart');
    if (!ctx) return;
    
    // Destroy existing chart
    if (this.charts.categoryBreakdown) {
      this.charts.categoryBreakdown.destroy();
    }
    
    const categories = [CF_CATEGORIES.OPERATING, CF_CATEGORIES.INVESTING, CF_CATEGORIES.FINANCING];
    const inflowData = categories.map(cat => yearSummary[cat].inflow);
    const outflowData = categories.map(cat => -yearSummary[cat].outflow);
    
    this.charts.categoryBreakdown = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: categories.map(cat => getCategoryDisplayName(cat)),
        datasets: [
          {
            label: '収入',
            data: inflowData,
            backgroundColor: 'rgba(76, 175, 80, 0.8)'
          },
          {
            label: '支出',
            data: outflowData,
            backgroundColor: 'rgba(244, 67, 54, 0.8)'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const value = Math.abs(context.parsed.y);
                return `${context.dataset.label}: ¥${this.formatNumber(value)}`;
              }
            }
          }
        },
        scales: {
          y: {
            ticks: {
              callback: (value) => '¥' + this.formatNumber(Math.abs(value))
            }
          }
        }
      }
    });
  }

  /**
   * Update detailed breakdown table
   * @param {Array} monthlySummaries - Monthly cashflow summaries
   */
  updateDetailedBreakdown(monthlySummaries) {
    const container = document.getElementById('detailedBreakdown');
    if (!container) return;
    
    let html = '<h2>月次詳細</h2>';
    html += '<div class="table-container">';
    html += '<table class="breakdown-table">';
    html += `
      <thead>
        <tr>
          <th>月</th>
          <th>営業CF</th>
          <th>投資CF</th>
          <th>財務CF</th>
          <th>合計</th>
        </tr>
      </thead>
      <tbody>
    `;
    
    monthlySummaries.forEach(month => {
      const operating = month[CF_CATEGORIES.OPERATING].net;
      const investing = month[CF_CATEGORIES.INVESTING].net;
      const financing = month[CF_CATEGORIES.FINANCING].net;
      const total = operating + investing + financing;
      
      html += `
        <tr>
          <td>${month.label}</td>
          <td class="${operating >= 0 ? 'positive' : 'negative'}">¥${this.formatNumber(operating)}</td>
          <td class="${investing >= 0 ? 'positive' : 'negative'}">¥${this.formatNumber(investing)}</td>
          <td class="${financing >= 0 ? 'positive' : 'negative'}">¥${this.formatNumber(financing)}</td>
          <td class="${total >= 0 ? 'positive' : 'negative'}"><strong>¥${this.formatNumber(total)}</strong></td>
        </tr>
      `;
    });
    
    html += '</tbody></table></div>';
    container.innerHTML = html;
  }

  /**
   * Format number with commas
   * @param {number} num - Number to format
   * @returns {string} - Formatted number
   */
  formatNumber(num) {
    return Math.round(num).toLocaleString('ja-JP');
  }

  /**
   * Show loading state
   */
  showLoading() {
    if (this.container) {
      this.container.innerHTML = '<div class="loading">読み込み中...</div>';
    }
  }

  /**
   * Show error message
   * @param {string} message - Error message
   */
  showError(message) {
    if (this.container) {
      this.container.innerHTML = `<div class="error">エラー: ${message}</div>`;
    }
  }

  /**
   * Event handlers (to be overridden)
   */
  onFiscalYearChange(year) {
    console.log('Fiscal year changed:', year);
  }

  onRefresh() {
    console.log('Refresh clicked');
  }
}

// Export singleton instance
export const cashFlowUI = new CashFlowUI();
